
import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useIsMobile } from '@/hooks/use-mobile';

const faqItems = [
  {
    question: "What is MindNote?",
    answer: "MindNote is an advanced, AI-powered notetaker designed to help you capture, organize, and enhance your ideas. Whether you need a notebook, notepad, or memo pad for quick thoughts, a journal for deeper reflections, or a sticky note or post-it for reminders, MindNote has you covered. This intelligent writing tool is available as a mobile app, web app, Kindle app, and iOS mobile app."
  },
  {
    question: "What platforms is MindNote available on?",
    answer: "MindNote is available as a native iOS mobile app, web and mobile web app, and Kindle app."
  },
  {
    question: "Does MindNote offer a free trial?",
    answer: "Yes! MindNote offers a 3-day free trial so you can explore all its features before committing to a subscription."
  },
  {
    question: "What subscription plans are available?",
    answer: "Monthly: $4.99 per month\nYearly: $50 (Save 16%)"
  },
  {
    question: "What are the key features of MindNote?",
    answer: "AI-powered note editing and organization\nSpeech-to-text and text-to-voice functionality\nCustomizable organization with folders and tags\nMedia support (images, videos, audio, etc.)\nCollaboration and real-time editing\nExporting in .pdf, .csv, .doc, and .docx\nSmart AI prompts for summarization, translation, and more"
  },
  {
    question: "Which use cases does MindNote have?",
    answer: "MindNote is a versatile tool that can be used for a wide range of purposes, including but not limited to: Work and professional meetings, Personal journaling and reflections, Research and all level academic studies, Creative writing and brainstorming, Project management and task tracking, Team collaboration and knowledge sharing, Medical notes and documentation, Business idea planning and strategy, Finance and budgeting notes, Language learning and translation assistance, Legal documentation and case study notes, Travel planning and itineraries, Shopping lists and personal to-do lists, Client and customer interactions, Content planning for social media and blogging, Exercise and meal plan. Among many other cases."
  },
  {
    question: "How do I start using MindNote?",
    answer: "Follow these simple steps to start taking notes with MindNote: Create a group – Organize your notes by creating groups for different topics or projects. Add an image – Attach an image to your note for visual reference. Add a title – Give your note a relevant title for easy identification. Start your note – Begin typing or use speech-to-text to capture your thoughts. Modify your note with AI** – Use AI prompts to edit, summarize, or format your note (this feature is available only after you start writing). Save your note – Ensure your note is saved for future reference. Share or export – Generate a shareable link or export your note as a .pdf, .doc, or .csv file."
  },
  {
    question: "Can I collaborate with others on MindNote?",
    answer: "Yes! You can share notes with colleagues or friends and collaborate in real-time."
  },
  {
    question: "Does MindNote support media attachments?",
    answer: "You can insert images, videos, and audio files into your notes."
  },
  {
    question: "How does the AI-powered editing feature work?",
    answer: "Once you start writing a note, you can edit it in seconds using AI prompts. Here are some examples of what you can do: Summarize the content, Reorganize text alphabetically, Convert notes into tables, Translate into different languages, Enhance clarity and readability, Add to your current ideas, Make calculations, solutions to problems. And many other possibilities! Simply write your note and let AI help you transform it instantly."
  },
  {
    question: "Can I export my notes?",
    answer: "Yes, you can export notes in various formats, including .pdf, .doc, .csv, and .docx."
  },
  {
    question: "I forgot my password. How can I reset it?",
    answer: "You can reset your password by clicking on \"Forgot Password?\" on the login screen and following the instructions."
  },
  {
    question: "I encountered a bug. How do I report it?",
    answer: "You can report issues through the app's support section or email our support team."
  },
  {
    question: "How do I cancel my subscription?",
    answer: "In web: You can manage or cancel your subscription through your account settings in FastSpring where you subscribed. In iOS mobile app: You can manage or cancel your subscription through your Apple ID subscriptions in the App Store."
  },
  {
    question: "Is there an enterprise version of MindNote available for businesses and teams?",
    answer: "An enterprise plan is also in development, making MindNote the perfect AI note solution for personal and professional use."
  }
];

const KnowledgeBase = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useIsMobile();
  
  // Filter FAQ items based on search term
  const filteredFAQs = faqItems.filter(item => 
    item.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
        <Input
          type="text"
          placeholder="Search knowledge base..."
          className="pl-9"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      
      <Accordion 
        type="single" 
        collapsible 
        className="w-full"
      >
        {filteredFAQs.map((item, index) => (
          <AccordionItem key={index} value={`item-${index}`}>
            <AccordionTrigger className={`text-left ${isMobile ? 'text-sm pr-3' : ''}`}>
              {item.question}
            </AccordionTrigger>
            <AccordionContent className={isMobile ? 'text-xs' : 'text-sm'}>
              <p className="whitespace-pre-line">{item.answer}</p>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {filteredFAQs.length === 0 && (
        <div className="text-center py-4">
          <p className="text-gray-500">No results found. Try a different search term.</p>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
